import { Button, InputNumber, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NO_TEAR_OUT, QUANTITY, TOOLTIP_PLACEMENT, UNIT_MEASURE } from '@common/constants';
import cssColors from '@common/css/cssColors.module.scss';
import formatUOM from '@common/helpers/formatUOM';
import { useShapeArea } from '@common/hooks/useShapeArea/useShapeArea';
import { TearOutModel } from '@common/models';
import { COMING_SOON_IMAGE, getProductDescription, handleIntegerNumberInput } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { SectionSubtitle } from '@core/components';
import ImageUtils from '@core/utils/ImageUtils';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSelectedTearOuts, updateSelectedTearOuts } from '@redux/slices/tearOutSlice';

import OptionalProductsSection from '../OptionalProductsSection/OptionalProductsSection';
import useTearOutCountertop from './useTearOutCountertop';

export function TearOutCountertop(): ReactElement {
    const { t } = useTranslation();
    const { totalBaseCountertopArea } = useShapeArea();
    const { areTearOutsAvailable, getTearOutByProductId, filteredSelectedTearOuts } = useTearOutCountertop();

    const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
    const { tearOuts, isFetchingOptionalItems } = useOptionalItems();

    const dispatch = useAppDispatch();

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const selectTearOut = (selectedItem: TearOutModel) => {
        const selectedTearOutsDraft = new Map(selectedTearOuts);

        // If any tearOut is clicked
        if (selectedItem.name !== NO_TEAR_OUT) {
            // Remove "no tear out" item from selection
            for (const selectedTearOutId of selectedTearOuts.keys()) {
                const selectedTearOut = getTearOutByProductId(selectedTearOutId);
                if (selectedTearOut?.name === NO_TEAR_OUT) {
                    selectedTearOutsDraft.set(selectedTearOut.productId, {
                        quantity: 0,
                        isSelected: false,
                        isManuallyChanged: false,
                    });
                }
            }
        }

        // If "no tear out" item is clicked
        if (selectedItem.name === NO_TEAR_OUT) {
            // Remove selected state for any other selected tearOut
            for (const [selectedTearOutId, selectedTearOutProperties] of selectedTearOutsDraft.entries()) {
                const selectedTearOut = getTearOutByProductId(selectedTearOutId);
                if (selectedTearOut?.name !== NO_TEAR_OUT) {
                    // If the selected tearOut was manually changed, preserve the value inputed by the user
                    if (selectedTearOutProperties.isManuallyChanged) {
                        selectedTearOutsDraft.set(selectedTearOutId, {
                            ...selectedTearOutProperties,
                            isSelected: false,
                        });
                    } else {
                        selectedTearOutsDraft.delete(selectedTearOutId);
                    }
                }
            }
        }

        const currSelectedTearOutProps = selectedTearOuts.get(selectedItem.productId);
        // If the current item is already selected
        if (currSelectedTearOutProps) {
            if (currSelectedTearOutProps.isManuallyChanged || selectedItem.name === NO_TEAR_OUT) {
                selectedTearOutsDraft.set(selectedItem.productId, {
                    quantity: selectedTearOuts.get(selectedItem.productId)?.quantity ?? 0,
                    isSelected: !currSelectedTearOutProps.isSelected,
                    isManuallyChanged: currSelectedTearOutProps.isManuallyChanged,
                });
            } else {
                selectedTearOutsDraft.delete(selectedItem.productId);
            }
        } else {
            // If the current clicked item is the first tearOut selected, it's quantity should be equal to the countertop area
            // If it's not the first to be selected, his initial quantity should be zero
            const selectedTearOutCount = filteredSelectedTearOuts.length;
            selectedTearOutsDraft.set(selectedItem.productId, {
                quantity: selectedTearOutCount || selectedItem.name === NO_TEAR_OUT ? 0 : totalBaseCountertopArea ?? 0,
                isSelected: true,
                isManuallyChanged: false,
            });
        }

        dispatch(updateSelectedTearOuts(selectedTearOutsDraft));
    };

    const updateQuantity = (tearOut: TearOutModel, newQuantity: number) => {
        if (newQuantity < 0) {
            return;
        }
        const newMap = new Map(selectedTearOuts);
        newMap.set(tearOut.productId, {
            quantity: Math.ceil(newQuantity),
            isSelected: Boolean(newMap.get(tearOut.productId)?.isSelected && newQuantity > 0),
            isManuallyChanged: true,
        });

        dispatch(updateSelectedTearOuts(newMap));
    };

    const changeQuantity = (tearOut: TearOutModel, amount: number): void => {
        const newMap = new Map(selectedTearOuts);
        const prevTearOutValue = newMap.get(tearOut.productId);

        if (prevTearOutValue) {
            const updatedQuantity = prevTearOutValue.quantity + amount;
            if (updatedQuantity > -1) {
                newMap.set(tearOut.productId, {
                    quantity: updatedQuantity,
                    isSelected: prevTearOutValue.isSelected && updatedQuantity > 0,
                    isManuallyChanged: true,
                });

                dispatch(updateSelectedTearOuts(newMap));
            }
        }
    };

    interface TearOutQuantityProps {
        tearOut: TearOutModel;
        quantity: number;
        unitOfMeasure: string;
    }

    const TearOutQuantity = ({ tearOut, quantity, unitOfMeasure }: TearOutQuantityProps) => {
        const [productQuantity, setProductQuantity] = useState(0);
        useEffect(() => {
            setProductQuantity(quantity);
        }, [quantity]);

        return (
            <div className="card-container__quantity">
                <Button
                    type={'text'}
                    style={{ color: productQuantity === 0 ? 'transparent' : 'inherit' }}
                    disabled={productQuantity === 0}
                    onClick={(e) => {
                        e.stopPropagation();
                        changeQuantity(tearOut, -1);
                    }}
                >
                    {QUANTITY.MINUS}
                </Button>
                <InputNumber
                    value={quantity}
                    onKeyDown={handleIntegerNumberInput}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(value) => setProductQuantity(value ? Math.floor(value) : 0)}
                    onPressEnter={() => updateQuantity(tearOut, productQuantity)}
                    onBlur={() => updateQuantity(tearOut, productQuantity)}
                />
                {unitOfMeasure}
                <Button
                    type={'text'}
                    onClick={(e) => {
                        e.stopPropagation();
                        changeQuantity(tearOut, 1);
                    }}
                >
                    {QUANTITY.PLUS}
                </Button>
            </div>
        );
    };

    interface TearOutItemProps {
        tearOut: TearOutModel;
    }

    const TearOutItem = ({ tearOut }: TearOutItemProps) => {
        const description = getProductDescription(tearOut);

        const isSelected = selectedTearOuts.get(tearOut.productId)?.isSelected;
        const hasQuantity = selectedTearOuts.get(tearOut.productId)?.isSelected && tearOut.name !== NO_TEAR_OUT;
        const unitOfMeasure = formatUOM(tearOut.unitOfMeasure || UNIT_MEASURE.TEAR_OUT);

        const isNoTearOut = tearOut.name === NO_TEAR_OUT;

        return (
            <div
                className={classNames('card-container', {
                    'multi-action-card': !isNoTearOut,
                    'single-action-card': isNoTearOut,
                    selected: isSelected,
                    'add-quantity': hasQuantity,
                })}
                onClick={() => selectTearOut(tearOut)}
            >
                <img
                    alt={description}
                    className="card-container__image"
                    src={ImageUtils.imgUrl(tearOut.imageUrl ?? COMING_SOON_IMAGE, 300)}
                />

                <Tooltip title={description} color={cssColors.toolTipColor} placement={TOOLTIP_PLACEMENT}>
                    <div className="card-container__name">{description}</div>
                </Tooltip>

                {tearOut.name !== NO_TEAR_OUT ? (
                    <>
                        <div className="card-container__price">
                            {formatPriceUnitOrFree(tearOut.price, unitOfMeasure)}
                        </div>
                        {selectedTearOuts?.get(tearOut.productId)?.isSelected ? (
                            <TearOutQuantity
                                tearOut={tearOut}
                                quantity={selectedTearOuts.get(tearOut.productId)?.quantity || 0}
                                unitOfMeasure={unitOfMeasure}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>
        );
    };

    const TearOutContent = () => {
        return isFetchingOptionalItems ? (
            <Spin style={{ fontSize: 24 }} />
        ) : (
            <div className="card-with-quantity card-with-quantity--large">
                {tearOuts.map((tearOut, id) => (
                    <TearOutItem key={id} tearOut={tearOut} />
                ))}
            </div>
        );
    };

    return (
        <>
            {areTearOutsAvailable() ? (
                <div>
                    <SectionSubtitle
                        name={t('SCROLLABLE_AREA.TEAR_OUT_COUNTERTOP.TITLE')}
                        description={t('SCROLLABLE_AREA.TEAR_OUT_COUNTERTOP.DESCRIPTION')}
                        showLine
                    />
                    <TearOutContent />
                    <OptionalProductsSection />
                </div>
            ) : (
                <></>
            )}
        </>
    );
}
